import React, { useState, useMemo, useEffect } from 'react';
import {
    Button,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    IconButton,
    Typography,
    Stack,
    RadioGroup,
    FormControlLabel,
    Radio,
    Autocomplete,
    Tooltip,
    Box,
    List,
    ListItem,
} from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { useTBContext } from '../Contexts/useTBStates';
import CreateApi from '../../shared/Settings/appSettings';
import type { EventPushMessage } from '@moment-golf/types';
import {
    resolveActiveMessageForIcon,
    resolveActiveMessages,
    resolveMessageHistory,
    EVENT_PUSH_ICON_LABELS,
} from '@moment-golf/types';
import {
    appendMessageToViewEvent,
    ICON_OPTIONS,
    ICON_MAP_LARGE,
    formatPushTimestamp,
    historyEntryLabel,
    targetLabel,
    type IconKey,
} from './pushMessageShared';

type TargetMode = 'all' | 'teeTime' | 'players';

interface TeeTimeOption {
    label: string;
    teeTime: string;
    round: number;
}

interface PlayerOption {
    label: string;
    scoreCode: string;
}

export const PushNotificationDialog = () => {
    const { viewEvent, setViewEvent, viewEventData } = useTBContext();
    const api = CreateApi();

    const [open, setOpen] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [historyIconFilter, setHistoryIconFilter] = useState<IconKey | null>(null);
    const [targetMode, setTargetMode] = useState<TargetMode>('all');
    const [selectedTeeTimes, setSelectedTeeTimes] = useState<TeeTimeOption[]>([]);
    const [selectedPlayers, setSelectedPlayers] = useState<PlayerOption[]>([]);
    const [selectedIcon, setSelectedIcon] = useState<IconKey>('info');
    const [message, setMessage] = useState('');
    const [pushing, setPushing] = useState(false);

    const messages = (viewEvent as any)?.eventPushMessages as EventPushMessage[] | undefined;

    const activeMessages = useMemo(() => resolveActiveMessages(messages), [messages]);

    const activeForSelectedIcon = useMemo(
        () => resolveActiveMessageForIcon(messages, selectedIcon),
        [messages, selectedIcon],
    );

    const history = useMemo(() => resolveMessageHistory(messages), [messages]);

    const teeTimeOptions = useMemo<TeeTimeOption[]>(() => {
        const seen = new Set<string>();
        const options: TeeTimeOption[] = [];
        const registrations = viewEventData?.registrations ?? [];
        const sorted = [...registrations].sort((a, b) =>
            (a.playerName ?? '').localeCompare(b.playerName ?? ''));

        for (const reg of sorted) {
            for (const slot of reg.teeTimes ?? []) {
                const teeTimeStr = typeof slot.teeTime === 'string'
                    ? slot.teeTime
                    : (slot.teeTime as Date).toISOString();
                const key = `${slot.round}-${teeTimeStr}`;
                if (!seen.has(key)) {
                    seen.add(key);
                    const displayTime = new Date(teeTimeStr).toLocaleTimeString([], {
                        hour: '2-digit', minute: '2-digit', timeZone: 'UTC',
                    });
                    options.push({
                        label: `Rd ${slot.round} · ${displayTime} — ${reg.playerName ?? reg.scoreCode}`,
                        teeTime: teeTimeStr,
                        round: slot.round,
                    });
                }
            }
        }
        return options.sort((a, b) => {
            if (a.round !== b.round) return a.round - b.round;
            return a.teeTime.localeCompare(b.teeTime);
        });
    }, [viewEventData]);

    const playerOptions = useMemo<PlayerOption[]>(() => {
        const registrations = viewEventData?.registrations ?? [];
        return [...registrations]
            .filter(r => !r.canceled && !r.withdrawn)
            .sort((a, b) => (a.playerName ?? '').localeCompare(b.playerName ?? ''))
            .map(r => ({ label: r.playerName ?? r.scoreCode, scoreCode: r.scoreCode }));
    }, [viewEventData]);

    const populateFromMessage = (source: EventPushMessage | null, preserveIcon = false) => {
        if (!source) {
            setTargetMode('all');
            setSelectedTeeTimes([]);
            setSelectedPlayers([]);
            setMessage('');
            if (!preserveIcon) setSelectedIcon('info');
            return;
        }

        setSelectedIcon(source.icon);
        setMessage(source.message);

        const t = source.target;
        if (t === 'all' || t == null) {
            setTargetMode('all');
            setSelectedTeeTimes([]);
            setSelectedPlayers([]);
        } else if (t.type === 'teeTime') {
            setTargetMode('teeTime');
            setSelectedPlayers([]);
            const slots = Array.isArray(t.slots)
                ? t.slots
                : (t as { teeTime?: string; round?: number }).teeTime != null
                    ? [{ teeTime: (t as { teeTime: string }).teeTime, round: (t as { round?: number }).round ?? 0 }]
                    : [];
            const matches = teeTimeOptions.filter(o =>
                slots.some(s => s.teeTime === o.teeTime && s.round === o.round)
            );
            setSelectedTeeTimes(matches);
        } else if (t.type === 'players') {
            setTargetMode('players');
            setSelectedTeeTimes([]);
            const scoreCodes = t.scoreCodes ?? [];
            const matches = playerOptions.filter(o => scoreCodes.includes(o.scoreCode));
            setSelectedPlayers(matches);
        }
    };

    useEffect(() => {
        if (!open) return;
        populateFromMessage(activeForSelectedIcon);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!open) return;
        populateFromMessage(activeForSelectedIcon, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIcon]);

    const handleClose = () => {
        if (pushing) return;
        setOpen(false);
        setHistoryOpen(false);
    };

    const buildTarget = (): EventPushMessage['target'] => {
        if (targetMode === 'all') return 'all';
        if (targetMode === 'teeTime') {
            return {
                type: 'teeTime',
                slots: selectedTeeTimes.map(o => ({ teeTime: o.teeTime, round: o.round })),
            };
        }
        return { type: 'players', scoreCodes: selectedPlayers.map(p => p.scoreCode) };
    };

    const canPush = message.trim().length > 0
        && (targetMode === 'all'
            || (targetMode === 'teeTime' && selectedTeeTimes.length > 0)
            || (targetMode === 'players' && selectedPlayers.length > 0));

    const handlePush = async () => {
        if (!viewEvent || !canPush) return;
        setPushing(true);
        try {
            const res = await api.post('/tb/PushEventMessage', {
                _id: (viewEvent as any)._id,
                message: { message: message.trim(), icon: selectedIcon, target: buildTarget() },
            });
            appendMessageToViewEvent(viewEvent, setViewEvent, {
                message: message.trim(),
                icon: selectedIcon,
                target: buildTarget(),
                pushedAt: res.data.pushedAt,
            });
            setMessage('');
        } catch (err) {
            console.error('Failed to push message:', err);
        } finally {
            setPushing(false);
        }
    };

    const handleClear = async (iconKey: IconKey) => {
        if (!viewEvent) return;
        setPushing(true);
        try {
            const res = await api.post('/tb/PushEventMessage', {
                _id: (viewEvent as any)._id,
                message: { message: '', icon: iconKey, target: 'all', clearScope: 'icon' },
            });
            appendMessageToViewEvent(viewEvent, setViewEvent, {
                message: '',
                icon: iconKey,
                target: 'all',
                clearScope: 'icon',
                pushedAt: res.data.pushedAt,
            });
        } catch (err) {
            console.error('Failed to clear message:', err);
        } finally {
            setPushing(false);
        }
    };

    return (
        <>
            <Tooltip title={viewEvent ? 'Push Notification' : 'Select an event first'}>
                <span>
                    <IconButton
                        disabled={!(viewEvent as any)?._id}
                        sx={{ width: 30, height: 30, borderRadius: 5 }}
                        onClick={() => setOpen(true)}
                    >
                        <CampaignIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </span>
            </Tooltip>

            <Dialog
                open={open}
                onClose={pushing ? undefined : handleClose}
                maxWidth={false}
                PaperProps={{
                    sx: {
                        display: 'flex',
                        flexDirection: 'row',
                        overflow: 'hidden',
                        maxWidth: 'calc(100vw - 64px)',
                        height: 'clamp(560px, 80vh, 920px)',
                    },
                }}
            >
                {/* ── Main compose panel (fixed width — never shifts) ── */}
                <Box sx={{ width: 'clamp(470px, 50vw, 780px)', flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <DialogTitle>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <span>Push Notification</span>
                            <Button
                                size="small"
                                variant={historyOpen ? 'contained' : 'outlined'}
                                startIcon={<HistoryIcon />}
                                onClick={() => setHistoryOpen((v) => !v)}
                                disabled={pushing}
                            >
                                History
                            </Button>
                        </Stack>
                    </DialogTitle>

                    <DialogContent>
                        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
                            {/* Active messages */}
                            {activeMessages.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                        Active Messages
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(2, 1fr)',
                                            gap: 1,
                                        }}
                                    >
                                        {activeMessages.map((msg) => (
                                            <Stack
                                                key={msg.icon}
                                                direction="column"
                                                spacing={0.5}
                                                sx={{
                                                    px: 1,
                                                    py: 0.5,
                                                    bgcolor: 'action.hover',
                                                    borderRadius: 1,
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                }}
                                            >
                                                {/* Icon · label — target · Clear all on one row */}
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    {ICON_MAP_LARGE[msg.icon]}
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                        {EVENT_PUSH_ICON_LABELS[msg.icon]}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                                                        {targetLabel(msg)}
                                                    </Typography>
                                                    <Tooltip title="Clear message">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                disabled={pushing}
                                                                onClick={() => handleClear(msg.icon)}
                                                                sx={{ flexShrink: 0 }}
                                                            >
                                                                <HighlightOffIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </Stack>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                                >
                                                    {msg.message}
                                                </Typography>
                                            </Stack>
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            <Stack spacing={0.5}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Send To</Typography>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <RadioGroup
                                        row
                                        value={targetMode}
                                        onChange={(e) => {
                                            setTargetMode(e.target.value as TargetMode);
                                            setSelectedTeeTimes([]);
                                            setSelectedPlayers([]);
                                        }}
                                        sx={{ flexShrink: 0 }}
                                    >
                                        <FormControlLabel value="all" control={<Radio size="small" />} label="All" />
                                        <FormControlLabel value="teeTime" control={<Radio size="small" />} label="Tee Time" />
                                        <FormControlLabel value="players" control={<Radio size="small" />} label="Player(s)" />
                                    </RadioGroup>

                                    {targetMode === 'teeTime' && (
                                        <Autocomplete
                                            multiple
                                            options={teeTimeOptions}
                                            getOptionLabel={(o) => o.label}
                                            value={selectedTeeTimes}
                                            onChange={(_, v) => setSelectedTeeTimes(v)}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Select Tee Time(s)" size="small" />
                                            )}
                                            isOptionEqualToValue={(a, b) => a.teeTime === b.teeTime && a.round === b.round}
                                            sx={{ flex: 1, minWidth: 0 }}
                                        />
                                    )}

                                    {targetMode === 'players' && (
                                        <Autocomplete
                                            multiple
                                            options={playerOptions}
                                            getOptionLabel={(o) => o.label}
                                            value={selectedPlayers}
                                            onChange={(_, v) => setSelectedPlayers(v)}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Select Player(s)" size="small" />
                                            )}
                                            isOptionEqualToValue={(a, b) => a.scoreCode === b.scoreCode}
                                            sx={{ flex: 1, minWidth: 0 }}
                                        />
                                    )}
                                </Stack>
                            </Stack>

                            <Stack spacing={0.5}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Icon</Typography>
                                <Stack direction="row" spacing={1}>
                                    {ICON_OPTIONS.map((opt) => (
                                        <Tooltip key={opt.key} title={opt.label}>
                                            <IconButton
                                                onClick={() => setSelectedIcon(opt.key)}
                                                sx={{
                                                    border: selectedIcon === opt.key ? '2px solid' : '2px solid transparent',
                                                    borderColor: selectedIcon === opt.key ? 'primary.main' : 'transparent',
                                                    borderRadius: 2,
                                                    backgroundColor: selectedIcon === opt.key ? 'action.selected' : 'transparent',
                                                    color: selectedIcon === opt.key ? 'primary.main' : 'text.secondary',
                                                }}
                                            >
                                                {opt.element}
                                            </IconButton>
                                        </Tooltip>
                                    ))}
                                </Stack>
                                {activeForSelectedIcon && (
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        Pushing will replace the current {EVENT_PUSH_ICON_LABELS[selectedIcon]} message.
                                    </Typography>
                                )}
                            </Stack>

                            <TextField
                                label="Message"
                                multiline
                                minRows={3}
                                maxRows={6}
                                fullWidth
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder="Enter the message to send to players..."
                            />
                        </Stack>
                    </DialogContent>

                    <DialogActions sx={{ justifyContent: 'flex-end', px: 3, pb: 2 }}>
                        <Stack direction="row" spacing={1}>
                            <Button onClick={handleClose} disabled={pushing}>Cancel</Button>
                            <Button
                                variant="contained"
                                onClick={handlePush}
                                disabled={!canPush || pushing}
                                startIcon={pushing ? <CircularProgress size={16} color="inherit" /> : <CampaignIcon />}
                            >
                                {pushing ? 'Pushing…' : 'Push'}
                            </Button>
                        </Stack>
                    </DialogActions>
                </Box>

                {/* ── History panel — expands dialog to the right, main panel never moves ── */}
                <Collapse
                    in={historyOpen}
                    orientation="horizontal"
                    unmountOnExit
                    sx={{
                        flexShrink: 0,
                        '& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner': {
                            height: '100%',
                        },
                    }}
                >
                    <Box
                        sx={{
                            width: 'clamp(210px, 22vw, 304px)',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            borderLeft: 1,
                            borderColor: 'divider',
                        }}
                    >
                        {/* Header row */}
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Message History
                            </Typography>
                            <IconButton onClick={() => setHistoryOpen(false)} aria-label="Close history">
                                <CloseIcon />
                            </IconButton>
                        </Stack>

                        {/* Icon filter strip */}
                        <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                            sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
                        >
                            {ICON_OPTIONS.map((opt) => {
                                const count = history.filter((m) => m.icon === opt.key).length;
                                const active = historyIconFilter === opt.key;
                                return (
                                    <Tooltip key={opt.key} title={`${opt.label} (${count})`}>
                                        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setHistoryIconFilter(active ? null : opt.key)
                                                }
                                                sx={{
                                                    border: '2px solid',
                                                    borderColor: active ? 'primary.main' : 'transparent',
                                                    borderRadius: 1.5,
                                                    bgcolor: active ? 'action.selected' : 'transparent',
                                                    color: active ? 'primary.main' : 'text.secondary',
                                                    opacity: count === 0 ? 0.35 : 1,
                                                }}
                                                disabled={count === 0}
                                            >
                                                {opt.element}
                                            </IconButton>
                                            {count > 0 && (
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: -4,
                                                        right: -4,
                                                        bgcolor: active ? 'primary.main' : 'text.secondary',
                                                        color: 'background.paper',
                                                        borderRadius: '50%',
                                                        width: 16,
                                                        height: 16,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: 10,
                                                        fontWeight: 700,
                                                        lineHeight: 1,
                                                        pointerEvents: 'none',
                                                    }}
                                                >
                                                    {count > 99 ? '99+' : count}
                                                </Box>
                                            )}
                                        </Box>
                                    </Tooltip>
                                );
                            })}
                            {historyIconFilter && (
                                <Tooltip title="Show all">
                                    <IconButton
                                        size="small"
                                        onClick={() => setHistoryIconFilter(null)}
                                        sx={{ ml: 'auto !important', color: 'text.secondary' }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Stack>

                        {/* Scrollable history list */}
                        <Box sx={{ flex: 1, overflowY: 'auto' }}>
                            {(() => {
                                const filtered = historyIconFilter
                                    ? history.filter((m) => m.icon === historyIconFilter)
                                    : history;
                                if (filtered.length === 0) {
                                    return (
                                        <Typography variant="body2" sx={{ p: 3, color: 'text.secondary' }}>
                                            {historyIconFilter
                                                ? `No ${EVENT_PUSH_ICON_LABELS[historyIconFilter]} messages in history.`
                                                : 'No messages have been sent for this event.'}
                                        </Typography>
                                    );
                                }
                                return (
                                    <List disablePadding>
                                        {filtered.map((msg) => (
                                            <ListItem
                                                key={msg.pushedAt}
                                                alignItems="flex-start"
                                                sx={{
                                                    flexDirection: 'column',
                                                    alignItems: 'stretch',
                                                    px: 2,
                                                    py: 1.5,
                                                    borderBottom: 1,
                                                    borderColor: 'divider',
                                                }}
                                            >
                                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                                    {ICON_MAP_LARGE[msg.icon] ?? ICON_MAP_LARGE.info}
                                                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                                        {targetLabel(msg)} · {formatPushTimestamp(msg.pushedAt)}
                                                    </Typography>
                                                </Stack>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: msg.message === '' ? 'text.secondary' : 'text.primary',
                                                        whiteSpace: 'pre-wrap',
                                                        fontStyle: msg.message === '' ? 'italic' : 'normal',
                                                    }}
                                                >
                                                    {historyEntryLabel(msg)}
                                                </Typography>
                                            </ListItem>
                                        ))}
                                    </List>
                                );
                            })()}
                        </Box>
                    </Box>
                </Collapse>
            </Dialog>
        </>
    );
};
